// src/components/ExcelImport.jsx — Tools → Excel import (products → `items`, or any collection)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import toast, { Toaster } from "react-hot-toast";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { app, auth, db, functions } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import MenuBar from "./MenuBar";
import HeaderBar from "./HeaderBar";

const PREVIEW_MAX = 40;
const GENERIC_PREVIEW_ROWS = 120;
const BATCH_SIZE = 400;
const MAX_IMPORT_FILE_BYTES = 20 * 1024 * 1024; // 20 MB
/** Max documents to load for the “browse collection” preview (Firestore read quota). */
const COLLECTION_DOCS_PREVIEW_LIMIT = 10000;
/** Max distinct field columns in the preview grid (very wide schemas get truncated + note). */
const COLLECTION_PREVIEW_MAX_FIELDS = 120;
const LARGE_SERVER_UPLOAD_MIN_BYTES = 20 * 1024 * 1024;

function serializeFirestoreValue(v) {
  if (v === undefined || v === null) return v;
  if (typeof v?.toDate === "function") {
    try {
      return v.toDate().toISOString();
    } catch {
      return String(v);
    }
  }
  if (v instanceof Date) return v.toISOString();
  if (Array.isArray(v)) return v.map((x) => serializeFirestoreValue(x));
  if (typeof v === "object" && v.constructor === Object) {
    const o = {};
    for (const [k, val] of Object.entries(v)) {
      o[k] = serializeFirestoreValue(val);
    }
    return o;
  }
  return v;
}

/** One spreadsheet cell: scalars as text; objects/arrays as JSON. */
function fieldValueToCellString(v) {
  if (v === undefined) return "";
  try {
    const s = serializeFirestoreValue(v);
    if (s === null || s === undefined) return "";
    if (typeof s === "object") return JSON.stringify(s);
    return String(s);
  } catch {
    return "(unserializable)";
  }
}

function escapeCsvCell(s) {
  const str = String(s ?? "");
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Load documents + field union (same cap as on-screen preview). */
async function fetchCollectionTableSnapshot(collectionName) {
  const trimmed = String(collectionName).trim();
  if (!trimmed) throw new Error("No collection name");
  const q = query(collection(db, trimmed), limit(COLLECTION_DOCS_PREVIEW_LIMIT));
  const snap = await getDocs(q);
  const keySet = new Set();
  for (const d of snap.docs) {
    const data = d.data();
    if (data && typeof data === "object") {
      Object.keys(data).forEach((k) => keySet.add(k));
    }
  }
  let fieldKeys = Array.from(keySet).sort((a, b) => a.localeCompare(b));
  const fieldTrunc = fieldKeys.length > COLLECTION_PREVIEW_MAX_FIELDS;
  if (fieldTrunc) {
    fieldKeys = fieldKeys.slice(0, COLLECTION_PREVIEW_MAX_FIELDS);
  }
  const rows = snap.docs.map((d) => {
    const data = d.data() || {};
    const cells = {};
    for (const k of fieldKeys) {
      cells[k] = Object.prototype.hasOwnProperty.call(data, k) ? fieldValueToCellString(data[k]) : "";
    }
    return { id: d.id, cells };
  });
  rows.sort((a, b) => a.id.localeCompare(b.id));
  return {
    fieldKeys,
    rows,
    docCount: snap.size,
    truncatedDocs: snap.size >= COLLECTION_DOCS_PREVIEW_LIMIT,
    fieldTrunc,
  };
}

/** CSV: first column `id` (document id); other columns = fields (exclude duplicate `id` field name). */
function buildCollectionCsv(rows, fieldKeys) {
  const filtered = fieldKeys.filter((k) => k.toLowerCase() !== "id");
  const lines = [];
  lines.push(["id", ...filtered].map(escapeCsvCell).join(","));
  for (const row of rows) {
    const vals = [row.id, ...filtered.map((k) => row.cells[k] ?? "")];
    lines.push(vals.map(escapeCsvCell).join(","));
  }
  return lines.join("\r\n");
}

/** Excel-style column letters: A, B, … Z, AA, AB … */
function excelColumnLetter(index) {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const listCollectionsFn = httpsCallable(functions, "listFirestoreCollections");
const importSpreadsheetFromStorageFn = httpsCallable(functions, "importSpreadsheetFromStorage");
const storage = getStorage(app);

/** Callable payload shape can vary; normalize to sorted unique strings. */
function normalizeCollectionList(payload) {
  const d = payload?.data ?? payload;
  let raw = d?.collections;
  if (!Array.isArray(raw) && d && typeof d === "object" && Array.isArray(d.collections)) {
    raw = d.collections;
  }
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const x of raw) {
    const s = String(x ?? "").trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  out.sort((a, b) => a.localeCompare(b));
  return out;
}

/** Used when the callable fails (offline, not deployed) so the page still works — type any name in the field. */
const CLIENT_COLLECTION_FALLBACK = [
  "auditLogs",
  "brands",
  "customer_entries",
  "customers",
  "employeeCommissions",
  "Employees",
  "employees",
  "Employees1",
  "employees1",
  "Employees3",
  "employees3",
  "groups",
  "gstRates",
  "holidays",
  "hsnCodes",
  "items",
  "sales_import_months",
  "orders",
  "orderTypes",
  "partyDiscounts",
  "purchaseOrders",
  "staff",
  "suppliers",
  "syncQueue",
  "transactionClasses",
  "units",
  "users",
  "vouchers",
];

/** Lowercase trimmed keys for flexible Excel headers */
function normalizeKeys(row) {
  const out = {};
  if (!row || typeof row !== "object") return out;
  Object.keys(row).forEach((k) => {
    const key = String(k).toLowerCase().trim().replace(/\s+/g, " ");
    out[key] = row[k];
  });
  return out;
}

const ID_HEADER_KEYS = new Set(["id", "_id", "docid", "__documentid", "documentid", "firebase id"]);

function sanitizeFieldKey(k) {
  const s = String(k).trim().replace(/\./g, "_");
  return s || "field";
}

function coerceCellValue(raw) {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  const low = s.toLowerCase();
  if (low === "true") return true;
  if (low === "false") return false;
  if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
    try {
      return JSON.parse(s);
    } catch {
      /* string */
    }
  }
  if (/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) return n;
  }
  return s;
}

function extractDocId(norm) {
  for (const key of ID_HEADER_KEYS) {
    if (norm[key] !== undefined && norm[key] !== null && String(norm[key]).trim() !== "") {
      return String(norm[key]).trim();
    }
  }
  return "";
}

/** Build Firestore payload from normalized row (id columns excluded). */
function normToFirestoreData(norm) {
  const out = {};
  for (const [k, v] of Object.entries(norm)) {
    if (ID_HEADER_KEYS.has(k)) continue;
    const key = sanitizeFieldKey(k);
    const val = coerceCellValue(v);
    if (val !== null) out[key] = val;
  }
  return out;
}

function num(v, fallback = 0) {
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : fallback;
}

function gstString(norm) {
  const raw = norm.gst ?? norm["gst %"] ?? norm.gstpercent ?? norm.tax;
  if (raw === undefined || raw === null || raw === "") return "28%";
  const s = String(raw).trim();
  if (!s) return "28%";
  return s.includes("%") ? s : `${s}%`;
}

/**
 * Map a normalized row to Firestore item payload (aligned with Add Product / legacy import).
 * @param {Record<string, unknown>} norm
 * @param {number} slNo
 */
function rowToPayload(norm, slNo) {
  const code = String(norm.code ?? norm.itemcode ?? norm["item code"] ?? norm.sku ?? "").trim();
  const desc = String(
    norm.description ?? norm.desc ?? norm.name ?? norm.itemname ?? norm["item name"] ?? ""
  ).trim();

  return {
    code,
    desc,
    group: String(norm.group ?? "").trim(),
    unit: String(norm.unit ?? norm.uom ?? "").trim(),
    brand: String(norm.brand ?? "").trim(),
    gst: gstString(norm),
    hsn: String(norm.hsn ?? "").trim(),
    affectStock: /^n/i.test(String(norm.affectstock ?? norm["affect stock"] ?? "Y").trim()) ? "N" : "Y",
    stdPurchase: num(norm.purchase ?? norm.stdpurchase ?? norm["std purchase"]),
    stdCost: num(norm.cost ?? norm.stdcost ?? norm["std cost"]),
    stdSales: num(norm.sales ?? norm.stdsales ?? norm["std sales"] ?? norm.rate),
    mrp: num(norm.mrp),
    ws: num(norm.ws ?? norm.wholesale),
    bulk: num(norm.bulk),
    mech: num(norm.mech),
    reorderLevel: num(norm.reorder ?? norm.reorderlevel ?? norm["reorder level"]),
    maxStock: num(norm.maxstock ?? norm["max stock"]),
    minStock: num(norm.minstock ?? norm["min stock"]),
    location: String(norm.location ?? "").trim(),
    slNo,
  };
}

/** True if sheet_to_json produced at least one row with a non-empty cell. */
function sheetJsonHasData(json) {
  if (!Array.isArray(json) || !json.length) return false;
  return json.some((row) => {
    if (!row || typeof row !== "object") return false;
    return Object.values(row).some((v) => String(v ?? "").trim() !== "");
  });
}

/** Trim leading completely empty rows from AOA grid. */
function trimLeadingEmptyAoaRows(aoa) {
  if (!Array.isArray(aoa)) return [];
  let i = 0;
  while (i < aoa.length && (!aoa[i] || !aoa[i].some((c) => String(c ?? "").trim() !== ""))) i++;
  return aoa.slice(i);
}

/**
 * When sheet_to_json returns [] (e.g. title rows before headers), treat sheet as AOA and try
 * each of the first rows as the header row until we get at least one data row.
 */
function aoaToObjectsWithHeaderDetection(aoa) {
  const rows = trimLeadingEmptyAoaRows(aoa);
  if (rows.length < 2) return [];

  const maxHeaderTry = Math.min(rows.length - 1, 50);
  for (let headerIdx = 0; headerIdx < maxHeaderTry; headerIdx++) {
    const headerCells = Array.isArray(rows[headerIdx]) ? rows[headerIdx] : [];
    const used = new Set();
    const headerRow = headerCells.map((h, j) => {
      let s = String(h ?? "").trim();
      if (!s) s = `Column${j + 1}`;
      let key = s;
      let n = 2;
      while (used.has(key)) {
        key = `${s}_${n++}`;
      }
      used.add(key);
      return key;
    });

    const out = [];
    for (let r = headerIdx + 1; r < rows.length; r++) {
      const line = Array.isArray(rows[r]) ? rows[r] : [];
      const o = {};
      let any = false;
      headerRow.forEach((key, j) => {
        const v = line[j];
        const s = v === undefined || v === null ? "" : String(v);
        if (s.trim()) any = true;
        o[key] = s;
      });
      if (any) out.push(o);
    }
    if (out.length) return out;
  }
  return [];
}

/**
 * Read first sheet that has data. Tries every sheet (not only Sheet1) and a header-row fallback
 * when the first rows are titles/blank (common cause of “empty sheet” with real data below).
 */
function parseWorkbook(file) {
  return new Promise((resolve, reject) => {
    const isCsv = /\.csv$/i.test(file.name || "");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let workbook;
        if (isCsv) {
          const text = String(e.target.result ?? "");
          workbook = XLSX.read(text, { type: "string", cellDates: true });
        } else {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: "array", cellDates: true });
        }
        const names = workbook.SheetNames || [];
        if (!names.length) {
          reject(new Error("Workbook has no sheets."));
          return;
        }

        const opts = { defval: "", raw: false, blankrows: false };

        for (const sheetName of names) {
          const sheet = workbook.Sheets[sheetName];
          if (!sheet) continue;
          let json = XLSX.utils.sheet_to_json(sheet, opts);
          if (sheetJsonHasData(json)) {
            resolve({ json, sheetName });
            return;
          }
          const aoa = XLSX.utils.sheet_to_json(sheet, { ...opts, header: 1 });
          json = aoaToObjectsWithHeaderDetection(aoa);
          if (sheetJsonHasData(json)) {
            resolve({ json, sheetName });
            return;
          }
        }

        resolve({ json: [], sheetName: names[0] || "" });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("File read failed"));
    if (isCsv) reader.readAsText(file, "UTF-8");
    else reader.readAsArrayBuffer(file);
  });
}

const EMPTY_SHEET_HINT =
  "No data rows found. Use a sheet whose first header row has column names (or move data off a title-only first row). For multi-sheet workbooks, any sheet with data is used. CSV: save as UTF-8.";
const LARGE_FILE_HINT =
  "File is too large for browser import (max 20 MB). Split into smaller files (by month/rows) and upload in parts.";

function downloadTemplateCsv() {
  const headers = [
    "code",
    "Description",
    "Group",
    "Unit",
    "Brand",
    "GST",
    "HSN",
    "Purchase",
    "Cost",
    "Sales",
    "MRP",
    "WS",
    "Bulk",
    "Mech",
    "reorderLevel",
    "maxStock",
    "minStock",
    "location",
  ];
  const example = [
    "DEMO001",
    "Sample product",
    "GENERAL",
    "PCS",
    "DEMO",
    "18%",
    "1234",
    "100",
    "80",
    "150",
    "200",
    "0",
    "0",
    "0",
    "5",
    "100",
    "2",
    "A1",
  ];
  const lines = [headers.join(","), example.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "product-import-template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
  toast.success("Template downloaded");
}

export default function ExcelImport() {
  const fileRef = useRef(null);
  const genericFileRef = useRef(null);
  const [uiTab, setUiTab] = useState("items"); // items | any

  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [parsed, setParsed] = useState([]);
  const [sheetName, setSheetName] = useState("");
  const [importMode, setImportMode] = useState("insert");
  const [progress, setProgress] = useState("");

  const [collections, setCollections] = useState(() => [...CLIENT_COLLECTION_FALLBACK]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  /** Info or warning about how the list was loaded (server vs fallback). */
  const [collectionsBanner, setCollectionsBanner] = useState("");
  const [targetCollection, setTargetCollection] = useState("");
  const [genericFileName, setGenericFileName] = useState("");
  const [genericSheetName, setGenericSheetName] = useState("");
  /** @type {{ line: number, docId: string, cells: Record<string, string>, issues: string[] }[]} */
  const [genericRows, setGenericRows] = useState([]);
  const [genericHeaders, setGenericHeaders] = useState([]);
  const [genericWriteMode, setGenericWriteMode] = useState("merge"); // merge | replace
  const [genericProgress, setGenericProgress] = useState("");
  const [largeServerFile, setLargeServerFile] = useState(null);
  const [largeServerFileName, setLargeServerFileName] = useState("");
  const [largeServerUploadProgress, setLargeServerUploadProgress] = useState("");
  const [largeServerImporting, setLargeServerImporting] = useState(false);
  /** @type {{ id: string, cells: Record<string, string> }[]} */
  const [collectionPreviewDocs, setCollectionPreviewDocs] = useState([]);
  const [collectionPreviewFieldKeys, setCollectionPreviewFieldKeys] = useState([]);
  const [collectionPreviewLoading, setCollectionPreviewLoading] = useState(false);
  const [collectionPreviewError, setCollectionPreviewError] = useState("");
  const [collectionPreviewHint, setCollectionPreviewHint] = useState("");
  const [docListFilter, setDocListFilter] = useState("");
  const [exportCsvLoading, setExportCsvLoading] = useState(false);

  const stats = useMemo(() => {
    let valid = 0;
    let invalid = 0;
    for (const p of parsed) {
      if (p.ok) valid++;
      else invalid++;
    }
    return { valid, invalid, total: parsed.length };
  }, [parsed]);

  const genericStats = useMemo(() => {
    let ok = 0;
    let bad = 0;
    for (const r of genericRows) {
      if (r.issues.length) bad++;
      else ok++;
    }
    return { ok, bad, total: genericRows.length };
  }, [genericRows]);

  const targetInLoadedList = useMemo(
    () => collections.some((c) => c === targetCollection.trim()),
    [collections, targetCollection]
  );

  const filteredPreviewDocs = useMemo(() => {
    const q = docListFilter.trim().toLowerCase();
    if (!q) return collectionPreviewDocs;
    return collectionPreviewDocs.filter((row) => {
      if (row.id.toLowerCase().includes(q)) return true;
      for (const v of Object.values(row.cells)) {
        if (String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [collectionPreviewDocs, docListFilter]);

  const downloadCollectionCsv = useCallback(async () => {
    const name = targetCollection.trim();
    if (!name) {
      toast.error("Select a Firestore collection first (step 2).");
      return;
    }
    setExportCsvLoading(true);
    try {
      const { fieldKeys, rows, docCount, truncatedDocs, fieldTrunc } = await fetchCollectionTableSnapshot(name);
      const csv = buildCollectionCsv(rows, fieldKeys);
      const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
      const safe = name.replace(/[^a-zA-Z0-9_-]/g, "_") || "collection";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${safe}-export.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      const capNote = truncatedDocs ? ` (first ${COLLECTION_DOCS_PREVIEW_LIMIT} documents only)` : "";
      const fieldNote = fieldTrunc ? ` Up to ${COLLECTION_PREVIEW_MAX_FIELDS} field columns.` : "";
      toast.success(
        docCount === 0
          ? `Exported CSV with header only (no documents yet).${fieldNote}`
          : `Downloaded ${docCount} row(s)${capNote}.${fieldNote}`
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not export collection. Check rules and network.");
    } finally {
      setExportCsvLoading(false);
    }
  }, [targetCollection]);

  useEffect(() => {
    setDocListFilter("");
    const name = targetCollection.trim();
    if (!name || uiTab !== "any") {
      setCollectionPreviewDocs([]);
      setCollectionPreviewFieldKeys([]);
      setCollectionPreviewError("");
      setCollectionPreviewHint("");
      return;
    }
    let cancelled = false;
    setCollectionPreviewLoading(true);
    setCollectionPreviewError("");
    setCollectionPreviewHint("");
    (async () => {
      try {
        const { fieldKeys, rows, docCount, truncatedDocs, fieldTrunc } = await fetchCollectionTableSnapshot(name);
        if (cancelled) return;
        setCollectionPreviewFieldKeys(fieldKeys);
        setCollectionPreviewDocs(rows);
        const fieldNote = fieldTrunc
          ? ` Showing up to ${COLLECTION_PREVIEW_MAX_FIELDS} field columns (union of keys across loaded docs).`
          : "";
        if (truncatedDocs) {
          setCollectionPreviewHint(
            `Showing the first ${COLLECTION_DOCS_PREVIEW_LIMIT} documents (read limit). There may be more in Firestore.${fieldNote}`
          );
        } else {
          setCollectionPreviewHint(
            `${docCount} document${docCount === 1 ? "" : "s"} in this collection.${fieldNote}`
          );
        }
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setCollectionPreviewDocs([]);
        setCollectionPreviewFieldKeys([]);
        setCollectionPreviewError(e?.message || String(e));
        setCollectionPreviewHint("");
      } finally {
        if (!cancelled) setCollectionPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetCollection, uiTab]);

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    setCollectionsBanner("");
    try {
      const res = await listCollectionsFn({});
      const list = normalizeCollectionList(res);
      if (!list.length) {
        throw new Error("Server returned no collection names");
      }
      const d = res?.data ?? {};
      const source = d.source;
      const warning = d.warning;
      setCollections(list);
      setTargetCollection((prev) => {
        const t = prev.trim();
        if (t && list.some((x) => x === t)) return t;
        return list[0] || "";
      });
      if (source === "fallback" && warning) {
        setCollectionsBanner(
          `Server could not enumerate collections (${warning}). Showing a built-in list — you can type any root collection name in the field below.`
        );
        toast.error("Using fallback collection list (see note below).");
      } else if (source === "fallback") {
        setCollectionsBanner(
          "Using server fallback list. Deploy the latest functions for a full list, or type any collection name."
        );
        toast("Loaded fallback collection list.", { duration: 4000 });
      } else {
        toast.success(`Loaded ${list.length} collection(s)`);
      }
    } catch (e) {
      console.error(e);
      const code = String(e?.code || "");
      const msg = e?.message || String(e);
      const permDenied =
        code.includes("permission-denied") ||
        /permission-denied|tools permission/i.test(msg);
      const unauthenticated = code.includes("unauthenticated");

      setCollections([...CLIENT_COLLECTION_FALLBACK]);
      setTargetCollection((prev) => prev.trim() || CLIENT_COLLECTION_FALLBACK[0] || "");

      if (unauthenticated) {
        setCollectionsBanner(
          "Sign in to load the live collection list from the server. You can still type any collection name below."
        );
        toast.error("Sign in to load collections from the server.");
      } else if (permDenied) {
        setCollectionsBanner(
          "Tools permission missing on your session. Ask an admin to grant Tools access, then sign out and sign in again."
        );
        toast.error("Permission denied — try signing out and back in after Tools access is granted.");
      } else if (code.includes("not-found") || code.includes("NOT_FOUND")) {
        setCollectionsBanner(
          "listFirestoreCollections is not deployed. Run: firebase deploy --only functions:listFirestoreCollections"
        );
        toast.error("Function not found — deploy Cloud Functions or use the built-in list.");
      } else {
        setCollectionsBanner(
          `Could not load collections (${msg}). Using a built-in list — type the exact collection name if needed.`
        );
        toast.error("Could not reach the server — using built-in list. Check network or deploy functions.");
      }
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (uiTab !== "any") return;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setCollectionsBanner("Sign in to load the full collection list from the server.");
        setCollections([...CLIENT_COLLECTION_FALLBACK]);
        return;
      }
      loadCollections();
    });
    return () => unsub();
  }, [uiTab, loadCollections]);

  useEffect(() => {
    setGenericRows((prev) =>
      prev.map((r) => ({
        ...r,
        issues: !targetCollection.trim() ? ["Pick a collection"] : [],
      }))
    );
  }, [targetCollection]);

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetGenericFile = () => {
    if (genericFileRef.current) genericFileRef.current.value = "";
  };

  const clearLargeServerFile = () => {
    setLargeServerFile(null);
    setLargeServerFileName("");
    setLargeServerUploadProgress("");
    if (genericFileRef.current) genericFileRef.current.value = "";
  };

  const buildParsedRows = useCallback((json) => {
    const seenCodes = new Map();
    const out = [];
    let maxSlPlaceholder = 0;

    json.forEach((row, idx) => {
      const norm = normalizeKeys(row);
      const payload = rowToPayload(norm, maxSlPlaceholder + 1);
      const line = idx + 2;
      const issues = [];

      if (!payload.code) {
        issues.push("Missing code");
      }
      if (seenCodes.has(payload.code.toLowerCase())) {
        issues.push(`Duplicate code in file (row ${seenCodes.get(payload.code.toLowerCase())})`);
      } else if (payload.code) {
        seenCodes.set(payload.code.toLowerCase(), line);
      }

      const ok = issues.length === 0 && !!payload.code;
      if (ok) maxSlPlaceholder++;

      out.push({
        line,
        ok,
        issues,
        payload,
      });
    });
    return out;
  }, []);

  const buildGenericRows = useCallback((json) => {
    const headersSet = new Set();
    json.forEach((row) => {
      const norm = normalizeKeys(row);
      Object.keys(norm).forEach((k) => headersSet.add(k));
    });
    const headers = Array.from(headersSet).sort((a, b) => {
      const ai = ID_HEADER_KEYS.has(a) ? 0 : 1;
      const bi = ID_HEADER_KEYS.has(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });

    const out = [];
    json.forEach((row, idx) => {
      const norm = normalizeKeys(row);
      const docId = extractDocId(norm);
      const cells = {};
      headers.forEach((h) => {
        const v = norm[h];
        cells[h] = v === undefined || v === null ? "" : String(v);
      });
      out.push({ line: idx + 2, docId, cells, issues: [] });
    });
    return { rows: out, headers };
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      toast.error(`${LARGE_FILE_HINT} Selected: ${(file.size / (1024 * 1024)).toFixed(1)} MB.`);
      resetFileInput();
      return;
    }
    setFileName(file.name);
    setParsed([]);
    setRawRows([]);

    try {
      const { json, sheetName: sn } = await parseWorkbook(file);
      setSheetName(sn);
      setRawRows(json);
      if (!json?.length) {
        toast.error(EMPTY_SHEET_HINT);
        resetFileInput();
        return;
      }
      const built = buildParsedRows(json);
      setParsed(built);
      toast.success(`Loaded ${json.length} row(s) from “${sn}”`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not read this file.");
      resetFileInput();
    }
  };

  const handleGenericFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > LARGE_SERVER_UPLOAD_MIN_BYTES) {
      setLargeServerFile(file);
      setLargeServerFileName(file.name);
      setGenericRows([]);
      setGenericHeaders([]);
      setGenericFileName("");
      setGenericSheetName("");
      setGenericProgress("");
      toast(`Large file detected (${(file.size / (1024 * 1024)).toFixed(1)} MB). Use server upload below.`, {
        duration: 5000,
      });
      return;
    }
    clearLargeServerFile();
    if (!targetCollection.trim()) {
      toast.error("Select a Firestore collection first.");
      resetGenericFile();
      return;
    }
    setGenericFileName(file.name);
    setGenericRows([]);
    setGenericHeaders([]);

    try {
      const { json, sheetName: sn } = await parseWorkbook(file);
      setGenericSheetName(sn);
      if (!json?.length) {
        toast.error(EMPTY_SHEET_HINT);
        resetGenericFile();
        return;
      }
      const { rows, headers } = buildGenericRows(json);
      setGenericRows(rows);
      setGenericHeaders(headers);
      toast.success(`Loaded ${json.length} row(s) — edit cells below, then upload.`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not read this file.");
      resetGenericFile();
    }
  };

  const runLargeServerImport = async () => {
    const name = targetCollection.trim();
    if (!name) {
      toast.error("Select a Firestore collection first.");
      return;
    }
    if (!largeServerFile) {
      toast.error("Choose a large file first.");
      return;
    }

    const uid = auth.currentUser?.uid || "anonymous";
    const safeName = largeServerFile.name.replace(/[^\w.-]+/g, "_");
    if (!/\.csv$/i.test(safeName)) {
      toast.error("Server import for very large files supports CSV only. Save as UTF-8 CSV and upload that file.");
      return;
    }
    const path = `excel-imports/${uid}/${Date.now()}-${safeName}`;
    const sref = storageRef(storage, path);
    const task = uploadBytesResumable(sref, largeServerFile);
    setLargeServerImporting(true);
    setImporting(true);
    setLargeServerUploadProgress("Uploading file to Firebase Storage…");

    try {
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = snap.totalBytes
              ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
              : 0;
            setLargeServerUploadProgress(`Uploading: ${pct}%`);
          },
          (err) => reject(err),
          () => resolve()
        );
      });

      setLargeServerUploadProgress("File uploaded. Importing on server in chunks…");
      // Optional: ensure upload succeeded and URL can be generated
      await getDownloadURL(sref);

      let startRow = 0;
      let rowsPerRun = 2500;
      let totalRows = 0;
      let totalCreated = 0;
      let totalWrittenById = 0;
      for (;;) {
        let res;
        try {
          res = await importSpreadsheetFromStorageFn({
            collectionName: name,
            storagePath: path,
            writeMode: genericWriteMode,
            startRow,
            maxRowsPerRun: rowsPerRun,
            deleteWhenDone: true,
          });
        } catch (chunkErr) {
          const code = String(chunkErr?.code || "");
          const msg = String(chunkErr?.message || chunkErr || "");
          if ((code.includes("deadline-exceeded") || msg.includes("deadline-exceeded")) && rowsPerRun > 500) {
            rowsPerRun = Math.max(500, Math.floor(rowsPerRun / 2));
            setLargeServerUploadProgress(
              `Chunk timeout at row ${startRow}. Retrying with smaller chunk (${rowsPerRun} rows)…`
            );
            continue;
          }
          throw chunkErr;
        }
        const d = res?.data || {};
        if (!d?.ok) {
          toast.error(d?.message || "Server import did not process rows.");
          break;
        }
        totalRows = Number(d.totalRows || totalRows || 0);
        totalCreated += Number(d.created || 0);
        totalWrittenById += Number(d.writtenById || 0);
        const end = Number(d.endRowExclusive || 0);
        setLargeServerUploadProgress(
          `Server import: ${Math.min(end, totalRows)} / ${totalRows} rows processed… (chunk ${rowsPerRun})`
        );
        if (!d.hasMore) {
          toast.success(
            `Server import done: ${totalRows} row(s), ${totalCreated} created, ${totalWrittenById} by ID.`
          );
          clearLargeServerFile();
          setGenericRows([]);
          setGenericHeaders([]);
          setGenericFileName("");
          setGenericSheetName("");
          break;
        }
        startRow = Number(d.nextStartRow || end || startRow + 12000);
      }
      setLargeServerUploadProgress("");
    } catch (err) {
      console.error(err);
      const code = err?.code ? String(err.code) : "";
      const details = err?.details ? String(err.details) : "";
      const msg = details || err?.message || String(err);
      toast.error(`Server import failed${code ? ` [${code}]` : ""}: ${msg}`);
      setLargeServerUploadProgress("");
    } finally {
      setLargeServerImporting(false);
      setImporting(false);
    }
  };

  const updateGenericCell = (rowIndex, headerKey, value) => {
    setGenericRows((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex], cells: { ...next[rowIndex].cells } };
      row.cells[headerKey] = value;
      const norm = {};
      Object.entries(row.cells).forEach(([k, v]) => {
        norm[k] = v;
      });
      const docId = extractDocId(norm);
      row.docId = docId;
      row.issues = [];
      if (!targetCollection.trim()) row.issues.push("Pick a collection");
      next[rowIndex] = row;
      return next;
    });
  };

  const runImport = async () => {
    const rows = parsed.filter((p) => p.ok);
    if (!rows.length) {
      toast.error("No valid rows to import.");
      return;
    }

    setImporting(true);
    setProgress("Loading existing items…");

    try {
      const itemsSnap = await getDocs(collection(db, "items"));
      const codeToId = new Map();
      let maxSl = 0;
      itemsSnap.forEach((d) => {
        const data = d.data() || {};
        const c = String(data.code ?? "").trim().toLowerCase();
        if (c) codeToId.set(c, d.id);
        const sl = Number(data.slNo) || 0;
        if (sl > maxSl) maxSl = sl;
      });

      try {
        const q = query(collection(db, "items"), orderBy("slNo", "desc"), limit(1));
        const lastSnap = await getDocs(q);
        if (!lastSnap.empty) {
          const top = Number(lastSnap.docs[0].data()?.slNo) || 0;
          if (top > maxSl) maxSl = top;
        }
      } catch {
        /* orderBy may fail if index missing */
      }

      let nextSl = maxSl;
      const ops = [];

      for (const r of rows) {
        const codeLower = r.payload.code.toLowerCase();
        const existingId = codeToId.get(codeLower);

        if (importMode === "insert") {
          if (existingId) {
            continue;
          }
          nextSl += 1;
          const data = {
            ...r.payload,
            slNo: nextSl,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          ops.push({ type: "create", data });
        } else {
          if (existingId) {
            const { slNo: _s, ...rest } = r.payload;
            ops.push({
              type: "update",
              id: existingId,
              data: {
                ...rest,
                updatedAt: serverTimestamp(),
              },
            });
          } else {
            nextSl += 1;
            ops.push({
              type: "create",
              data: {
                ...r.payload,
                slNo: nextSl,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
            });
          }
        }
      }

      const skippedDup =
        importMode === "insert"
          ? rows.filter((r) => codeToId.has(r.payload.code.toLowerCase())).length
          : 0;

      if (!ops.length) {
        toast.error(
          importMode === "insert"
            ? "All rows already exist (same code). Switch to “Update existing” or remove duplicates."
            : "Nothing to write."
        );
        setProgress("");
        return;
      }

      let done = 0;
      for (let i = 0; i < ops.length; i += BATCH_SIZE) {
        const chunk = ops.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const op of chunk) {
          if (op.type === "create") {
            const ref = doc(collection(db, "items"));
            batch.set(ref, op.data);
          } else {
            batch.update(doc(db, "items", op.id), op.data);
          }
        }
        setProgress(`Writing ${done + chunk.length} / ${ops.length}…`);
        await batch.commit();
        done += chunk.length;
      }

      const created = ops.filter((o) => o.type === "create").length;
      const updated = ops.filter((o) => o.type === "update").length;
      toast.success(
        `Import finished: ${created} created, ${updated} updated` +
          (skippedDup ? `, ${skippedDup} skipped (already in database)` : "")
      );
      setProgress("");
      setParsed([]);
      setRawRows([]);
      setFileName("");
      resetFileInput();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Import failed.");
      setProgress("");
    } finally {
      setImporting(false);
    }
  };

  const runGenericUpload = async () => {
    const name = targetCollection.trim();
    if (!name) {
      toast.error("Select a collection.");
      return;
    }
    const ready = genericRows.filter((r) => !r.issues.length);
    if (!ready.length) {
      toast.error("No rows to upload.");
      return;
    }

    setImporting(true);
    setGenericProgress("Preparing…");
    const merge = genericWriteMode === "merge";

    try {
      const ops = [];
      for (const r of ready) {
        const norm = {};
        Object.entries(r.cells).forEach(([k, v]) => {
          norm[k] = v;
        });
        const docId = extractDocId(norm);
        const data = normToFirestoreData(norm);
        data.updatedAt = serverTimestamp();
        if (!docId) {
          ops.push({ type: "create", data });
        } else {
          ops.push({ type: "set", id: docId, data, merge });
        }
      }

      let done = 0;
      for (let i = 0; i < ops.length; i += BATCH_SIZE) {
        const chunk = ops.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const op of chunk) {
          if (op.type === "create") {
            const ref = doc(collection(db, name));
            batch.set(ref, { ...op.data, createdAt: serverTimestamp() });
          } else {
            const ref = doc(db, name, op.id);
            batch.set(ref, op.data, { merge: op.merge });
          }
        }
        setGenericProgress(`Writing ${done + chunk.length} / ${ops.length}…`);
        await batch.commit();
        done += chunk.length;
      }

      const created = ops.filter((o) => o.type === "create").length;
      const written = ops.filter((o) => o.type === "set").length;
      toast.success(`Uploaded to “${name}”: ${created} new doc(s), ${written} write(s) by ID.`);
      setGenericProgress("");
      setGenericRows([]);
      setGenericHeaders([]);
      setGenericFileName("");
      resetGenericFile();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Upload failed. Check Firestore rules for this collection.");
      setGenericProgress("");
    } finally {
      setImporting(false);
    }
  };

  const clearPreview = () => {
    setParsed([]);
    setRawRows([]);
    setFileName("");
    setSheetName("");
    resetFileInput();
  };

  const clearGeneric = () => {
    setGenericRows([]);
    setGenericHeaders([]);
    setGenericFileName("");
    setGenericSheetName("");
    clearLargeServerFile();
    resetGenericFile();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />

      <main className="mx-auto max-w-6xl p-4 pb-24">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900">Excel import</h1>
          <p className="mt-1 text-sm text-slate-600">
            Import spreadsheet rows into Firestore. Use <strong>Products</strong> for the <code className="rounded bg-slate-100 px-1">items</code> catalog, or{" "}
            <strong>Any collection</strong> for bulk data with optional document IDs. For single products, use{" "}
            <Link to="/add-product" className="font-medium text-indigo-600 hover:underline">
              Add product
            </Link>
            .
          </p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          <button
            type="button"
            onClick={() => setUiTab("items")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              uiTab === "items" ? "bg-indigo-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"
            }`}
          >
            Products (items)
          </button>
          <button
            type="button"
            onClick={() => setUiTab("any")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              uiTab === "any" ? "bg-indigo-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"
            }`}
          >
            Any collection
          </button>
        </div>

        {uiTab === "items" && (
          <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <section>
              <h2 className="mb-2 text-sm font-semibold text-slate-800">Expected columns</h2>
              <p className="mb-2 text-xs text-slate-600">
                Minimum: <code className="rounded bg-slate-100 px-1">code</code>. Strongly recommended:{" "}
                <code className="rounded bg-slate-100 px-1">Description</code> (or desc / name). Numbers: Purchase, Cost,
                Sales, MRP, WS, Bulk, Mech. Optional: Group, Unit, Brand, GST, HSN, reorderLevel, maxStock, minStock,
                location.
              </p>
              <button
                type="button"
                onClick={downloadTemplateCsv}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100"
              >
                Download CSV template
              </button>
            </section>

            <section className="border-t border-slate-100 pt-5">
              <label className="mb-2 block text-sm font-semibold text-slate-800">1. Choose file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFile}
                disabled={importing}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700"
              />
              {fileName ? (
                <p className="mt-2 text-xs text-slate-500">
                  Selected: {fileName}
                  {sheetName ? ` · Sheet: ${sheetName}` : ""} · {rawRows.length} data row(s)
                </p>
              ) : null}
            </section>

            {parsed.length > 0 && (
              <section className="space-y-4 border-t border-slate-100 pt-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold text-slate-800">2. Preview & validate</h2>
                  <button type="button" onClick={clearPreview} className="text-sm text-slate-600 underline hover:text-slate-900">
                    Clear
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-xs text-slate-500">Total rows</p>
                    <p className="text-lg font-bold text-slate-900">{stats.total}</p>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <p className="text-xs text-emerald-800">Valid</p>
                    <p className="text-lg font-bold text-emerald-900">{stats.valid}</p>
                  </div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
                    <p className="text-xs text-rose-800">Invalid / duplicate</p>
                    <p className="text-lg font-bold text-rose-900">{stats.invalid}</p>
                  </div>
                  <div className="flex flex-col justify-center rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="mb-1 text-xs font-medium text-slate-600">Import mode</label>
                    <select
                      value={importMode}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                    >
                      <option value="insert">Add new only (skip existing codes)</option>
                      <option value="upsert">Update existing by code + add new</option>
                    </select>
                  </div>
                </div>

                <div className="max-h-[420px] overflow-x-auto overflow-y-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-slate-100">
                      <tr className="text-left text-slate-600">
                        <th className="px-2 py-2 font-semibold">#</th>
                        <th className="px-2 py-2 font-semibold">OK</th>
                        <th className="px-2 py-2 font-semibold">code</th>
                        <th className="px-2 py-2 font-semibold">Description</th>
                        <th className="px-2 py-2 font-semibold">Brand</th>
                        <th className="px-2 py-2 font-semibold">MRP</th>
                        <th className="px-2 py-2 font-semibold">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {parsed.slice(0, PREVIEW_MAX).map((r) => (
                        <tr key={r.line} className={r.ok ? "bg-white" : "bg-rose-50/50"}>
                          <td className="px-2 py-1.5 text-slate-500">{r.line}</td>
                          <td className="px-2 py-1.5">{r.ok ? "✓" : "✗"}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{r.payload.code || "—"}</td>
                          <td className="max-w-[200px] truncate px-2 py-1.5" title={r.payload.desc}>
                            {r.payload.desc || "—"}
                          </td>
                          <td className="px-2 py-1.5">{r.payload.brand || "—"}</td>
                          <td className="px-2 py-1.5 tabular-nums">{r.payload.mrp}</td>
                          <td className="px-2 py-1.5 text-rose-700">{r.issues.join("; ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsed.length > PREVIEW_MAX ? (
                  <p className="text-xs text-slate-500">
                    Showing first {PREVIEW_MAX} rows of {parsed.length}.
                  </p>
                ) : null}

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    disabled={stats.valid === 0 || importing}
                    onClick={runImport}
                    className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {importing ? "Importing…" : `Import ${stats.valid} valid row(s)`}
                  </button>
                  {progress ? <span className="text-sm text-slate-600">{progress}</span> : null}
                </div>
              </section>
            )}
          </div>
        )}

        {uiTab === "any" && (
          <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <header className="border-b border-slate-200 pb-4">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">Import to any Firestore collection</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                Choose a root collection, optionally download a blank CSV, then upload your spreadsheet to create or merge documents.
              </p>
            </header>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">1. Export or upload</h3>
              <p className="text-xs text-slate-600">
                In <strong>step 2</strong>, choose or type the collection name, then upload your Excel/CSV to write into Firestore.
                You can also use <strong>Download collection data (CSV)</strong> to export current docs, edit in Excel, and re-upload.
                Keep <code className="rounded bg-slate-100 px-1">id</code> to update existing docs; rows without{" "}
                <code className="rounded bg-slate-100 px-1">id</code> create new documents. Very large files (20MB+) switch to server import.
              </p>
            </section>

            <section className="rounded-lg border border-amber-100 bg-amber-50/90 p-3 text-xs text-amber-950">
              <strong className="font-semibold">Root collections only.</strong> Pick a name from the list or type one. Subcollections are not listed. Use a column named{" "}
              <code className="rounded bg-white px-1">id</code>, <code className="rounded bg-white px-1">docId</code>, or{" "}
              <code className="rounded bg-white px-1">__documentId</code> to update existing documents; rows without an ID create new documents with auto-generated IDs. Values: numbers, true/false, JSON objects/arrays in a cell, or text.
            </section>

            {collectionsBanner ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">{collectionsBanner}</div>
            ) : null}

            <section aria-labelledby="excel-collection-fields-heading">
              <h3 id="excel-collection-fields-heading" className="mb-2 text-sm font-semibold text-slate-800">
                2. Firestore collection
              </h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1 space-y-2">
                  <label className="text-xs font-semibold text-slate-700" htmlFor="excel-target-collection-select">
                    Collection name
                  </label>
                  <select
                    id="excel-target-collection-select"
                    value={targetInLoadedList ? targetCollection.trim() : ""}
                    onChange={(e) => setTargetCollection(e.target.value)}
                    disabled={collectionsLoading}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono"
                  >
                    <option value="">— Select a collection —</option>
                    {collections.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <div>
                    <label className="mb-1 block text-[10px] font-medium text-slate-600" htmlFor="excel-target-collection-custom">
                      Or type exact name (if not in list)
                    </label>
                    <input
                      id="excel-target-collection-custom"
                      type="text"
                      value={targetInLoadedList ? "" : targetCollection}
                      onChange={(e) => setTargetCollection(e.target.value)}
                      placeholder="e.g. myCollection"
                      disabled={collectionsLoading}
                      autoComplete="off"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {collections.length} collection name(s) in the list. Pick one above or type a custom root collection.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadCollections}
                  disabled={collectionsLoading}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  {collectionsLoading ? "Loading…" : "Refresh list"}
                </button>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">Write mode</label>
                  <select
                    value={genericWriteMode}
                    onChange={(e) => setGenericWriteMode(e.target.value)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="merge">Merge (set with merge)</option>
                    <option value="replace">Replace full document</option>
                  </select>
                </div>
              </div>
            </section>

            {!targetCollection.trim() ? (
              <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Select a Firestore collection to see the heading and upload a spreadsheet.
              </p>
            ) : null}

            {targetCollection.trim() ? (
              <div className="space-y-5 border-t border-slate-200 pt-5">
                <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/90 to-white px-4 py-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-800">Target Firestore collection</p>
                      <h2 className="mt-1 break-all font-mono text-xl font-bold tracking-tight text-indigo-950 md:text-2xl">
                        {targetCollection.trim()}
                      </h2>
                      <p className="mt-2 text-xs text-indigo-900/80">
                        Export matches the table below (up to {COLLECTION_DOCS_PREVIEW_LIMIT} documents,{" "}
                        {COLLECTION_PREVIEW_MAX_FIELDS} field columns max).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadCollectionCsv}
                      disabled={exportCsvLoading}
                      className="shrink-0 rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {exportCsvLoading ? "Preparing CSV…" : "Download collection data (CSV)"}
                    </button>
                  </div>
                </div>

              <section className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Existing documents in Firestore
                  </h3>
                  <input
                    type="search"
                    value={docListFilter}
                    onChange={(e) => setDocListFilter(e.target.value)}
                    placeholder="Filter by document ID or cell text…"
                    className="max-w-xs rounded-lg border border-slate-200 px-2 py-1 text-xs font-mono"
                  />
                </div>
                {collectionPreviewLoading ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-slate-600">
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                    Loading documents…
                  </div>
                ) : collectionPreviewError ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                    Could not read documents: {collectionPreviewError}
                    <br />
                    <span className="text-xs text-rose-700">Check Firestore security rules for this collection.</span>
                  </div>
                ) : (
                  <>
                    {collectionPreviewHint ? <p className="text-xs text-slate-600">{collectionPreviewHint}</p> : null}
                    <div className="max-h-[min(360px,45vh)] overflow-auto rounded border border-[#c6c6c6] bg-white shadow-sm">
                      <table
                        className="w-full min-w-[28rem] border-collapse text-left text-[11px]"
                        style={{ fontFamily: 'Calibri, "Segoe UI", system-ui, sans-serif' }}
                      >
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-[#E7E6E6]">
                            <th
                              rowSpan={2}
                              className="w-9 min-w-[2.25rem] border border-[#c6c6c6] bg-[#f3f3f3] px-1"
                              aria-hidden
                            />
                            <th className="border border-[#c6c6c6] px-2 py-1 text-center text-[10px] font-normal text-slate-600">
                              A
                            </th>
                            {collectionPreviewFieldKeys.map((_, i) => (
                              <th
                                key={`doc-col-letter-${i}`}
                                className="min-w-[5.5rem] border border-[#c6c6c6] px-1 py-1 text-center text-[10px] font-normal text-slate-600"
                              >
                                {excelColumnLetter(i + 1)}
                              </th>
                            ))}
                          </tr>
                          <tr className="bg-[#E7E6E6]">
                            <th className="border border-[#c6c6c6] px-2 py-1.5 text-left text-[11px] font-semibold text-slate-900">
                              Document ID
                            </th>
                            {collectionPreviewFieldKeys.map((k) => (
                              <th
                                key={k}
                                className="max-w-[14rem] border border-[#c6c6c6] px-1.5 py-1.5 text-left text-[11px] font-semibold text-slate-900"
                                title={k}
                              >
                                <span className="line-clamp-2 break-all">{k}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="font-mono text-[10px]">
                          {filteredPreviewDocs.length === 0 ? (
                            <tr>
                              <td
                                colSpan={2 + collectionPreviewFieldKeys.length}
                                className="border border-[#c6c6c6] px-2 py-4 text-center text-slate-500"
                              >
                                {collectionPreviewDocs.length === 0
                                  ? "No documents in this collection."
                                  : "No rows match the filter."}
                              </td>
                            </tr>
                          ) : (
                            filteredPreviewDocs.map((row, idx) => (
                              <tr key={row.id} className="bg-white">
                                <td className="border border-[#c6c6c6] bg-[#f3f3f3] px-1.5 py-1 text-right text-[10px] text-slate-700 tabular-nums">
                                  {idx + 1}
                                </td>
                                <td className="max-w-[12rem] border border-[#c6c6c6] align-top break-all px-2 py-1.5 text-xs font-semibold text-indigo-900">
                                  {row.id}
                                </td>
                                {collectionPreviewFieldKeys.map((k) => {
                                  const cell = row.cells[k] ?? "";
                                  return (
                                    <td
                                      key={k}
                                      className="max-w-[14rem] border border-[#c6c6c6] align-top whitespace-pre-wrap break-all px-1.5 py-1.5 text-[10px] text-slate-800"
                                      title={cell.length > 80 ? cell : undefined}
                                    >
                                      {cell || " "}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Upload spreadsheet</h3>
                <label className="mb-2 block text-xs text-slate-600" htmlFor="excel-generic-file">
                  Choose an Excel or CSV file. Row values appear in the preview below for editing before upload.
                </label>
                <input
                  id="excel-generic-file"
                  ref={genericFileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleGenericFile}
                  disabled={importing || !targetCollection}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700 disabled:opacity-50"
                />
                {genericFileName ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {genericFileName}
                    {genericSheetName ? ` · ${genericSheetName}` : ""} · {genericRows.length} row(s)
                  </p>
                ) : null}
                {largeServerFile ? (
                  <div className="mt-3 rounded-lg border border-emerald-300 bg-white/90 px-3 py-2">
                    <p className="text-xs text-emerald-900">
                      Large file selected: <strong>{largeServerFileName}</strong> ·{" "}
                      {(largeServerFile.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                    <p className="mt-1 text-[11px] text-emerald-900/80">
                      Browser preview is skipped. This will upload to Storage and import on the server. For very large imports, use UTF-8 CSV.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={runLargeServerImport}
                        disabled={largeServerImporting || !targetCollection.trim()}
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {largeServerImporting ? "Running server import…" : "Run server import"}
                      </button>
                      <button
                        type="button"
                        onClick={clearLargeServerFile}
                        disabled={largeServerImporting}
                        className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Remove file
                      </button>
                      {largeServerUploadProgress ? (
                        <span className="text-xs text-emerald-900/85">{largeServerUploadProgress}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </section>

            {genericRows.length > 0 && (
              <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Spreadsheet preview</h3>
                    <p className="text-xs text-slate-600">
                      Data will be written to <code className="rounded bg-white px-1 font-mono text-[11px]">{targetCollection.trim()}</code>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={importing || genericStats.ok === 0}
                      onClick={runGenericUpload}
                      className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-50"
                    >
                      {importing ? "Uploading to Firestore…" : `Upload to Firestore (${genericStats.ok} row${genericStats.ok === 1 ? "" : "s"})`}
                    </button>
                    <button type="button" onClick={clearGeneric} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      Clear
                    </button>
                  </div>
                </div>
                {genericProgress ? <p className="text-sm text-indigo-700">{genericProgress}</p> : null}
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
                    <div className="text-[10px] font-semibold uppercase text-slate-500">Rows</div>
                    <div className="text-lg font-bold">{genericStats.total}</div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
                    <div className="text-[10px] font-semibold uppercase text-emerald-800">Ready</div>
                    <div className="text-lg font-bold text-emerald-900">{genericStats.ok}</div>
                  </div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-center">
                    <div className="text-[10px] font-semibold uppercase text-rose-800">Issues</div>
                    <div className="text-lg font-bold text-rose-900">{genericStats.bad}</div>
                  </div>
                </div>

                <div className="max-h-[min(480px,50vh)] overflow-auto rounded border border-[#c6c6c6] bg-white shadow-sm">
                  <table
                    className="min-w-full border-collapse text-[11px]"
                    style={{ fontFamily: 'Calibri, "Segoe UI", system-ui, sans-serif' }}
                  >
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#E7E6E6]">
                        <th
                          rowSpan={2}
                          className="w-9 min-w-[2.25rem] border border-[#c6c6c6] bg-[#f3f3f3] px-1"
                          aria-hidden
                        />
                        {genericHeaders.map((_, i) => (
                          <th
                            key={`col-letter-${i}`}
                            className="min-w-[6rem] border border-[#c6c6c6] px-1 py-1 text-center text-[10px] font-normal text-slate-600"
                          >
                            {excelColumnLetter(i)}
                          </th>
                        ))}
                      </tr>
                      <tr className="bg-[#E7E6E6]">
                        {genericHeaders.map((h) => (
                          <th
                            key={h}
                            className="min-w-[7rem] border border-[#c6c6c6] px-1 py-1.5 text-left text-[11px] font-semibold text-slate-900"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {genericRows.slice(0, GENERIC_PREVIEW_ROWS).map((row, ri) => (
                        <tr key={row.line} className="bg-white">
                          <td className="border border-[#c6c6c6] bg-[#f3f3f3] px-1 py-0.5 text-right text-[10px] text-slate-700 tabular-nums">
                            {row.line}
                          </td>
                          {genericHeaders.map((h) => (
                            <td key={h} className="border border-[#c6c6c6] p-0">
                              <input
                                className="w-full min-w-[5rem] border-0 bg-transparent px-1 py-1 font-mono text-[10px] outline-none focus:bg-amber-50/80"
                                value={row.cells[h] ?? ""}
                                onChange={(e) => updateGenericCell(ri, h, e.target.value)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {genericRows.length > GENERIC_PREVIEW_ROWS ? (
                  <p className="text-xs text-slate-500">
                    Editable grid shows the first {GENERIC_PREVIEW_ROWS} rows; all {genericRows.length} rows are uploaded. Split the file or re-import to edit rows beyond the preview.
                  </p>
                ) : null}

                <p className="text-xs text-slate-500">
                  Edit cells if needed, then click <strong>Upload to Firestore</strong>. Your rules must allow writes to this collection.
                </p>
              </section>
            )}
              </div>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
